import { useEffect, useState, useRef  } from 'react';
import '../../dmView/style/App.css';
import '../../dmView/style/DiceStyle2.css';
import { useDice } from '../../../../providers/DiceProvider';
import diceSpinner from '../../dmView/pics/dice-spinner.svg';

const diceStrings = ['D100', 'D12', 'D10', 'D8', 'D6', 'D4', 'D20']

const Dice = ({openDice, setOpenDice}) => {

    const { diceResults, selectedDice, setSelectedDice, rollDice, isRolling } = useDice()
    const [dicePosition, setDicePosition] = useState(null);
    const diceWindowRef = useRef(null);
    const dragRef = useRef(null);
    const diceResultsMenuRef = useRef(null);

    useEffect(() => {
        const handlePointerMove = (event) => {
            if (!dragRef.current || !diceWindowRef.current) {
                return;
            }

            const { offsetX, offsetY } = dragRef.current;
            const { width, height } = diceWindowRef.current.getBoundingClientRect();
            const left = Math.max(0, Math.min(event.clientX - offsetX, window.innerWidth - width));
            const top = Math.max(0, Math.min(event.clientY - offsetY, window.innerHeight - height));

            setDicePosition({ left, top });
        };

        const handlePointerUp = () => {
            dragRef.current = null;
            document.body.classList.remove('diceDragging');
        };

        window.addEventListener('pointermove', handlePointerMove);
        window.addEventListener('pointerup', handlePointerUp);

        return () => {
            window.removeEventListener('pointermove', handlePointerMove);
            window.removeEventListener('pointerup', handlePointerUp);
        };  
    }, []);

    useEffect(() => {
        if (diceResultsMenuRef.current) {
            diceResultsMenuRef.current.scrollTop = 0;
        }
    }, [diceResults]);

    useEffect(() => {
        if(isRolling && !openDice) {
            setOpenDice(true)
        }
    }, [isRolling]);

    const selectDie = (diceKey) => {
        setSelectedDice(current => ({
            ...current,
            [diceKey]: (current[diceKey] || 0) + 1,
        }));
    };

    const handlePointerDown = (event) => {
        if (event.target.closest('button') || !diceWindowRef.current) {
            return;
        }

        const { left, top } = diceWindowRef.current.getBoundingClientRect();
        dragRef.current = {
            offsetX: event.clientX - left,
            offsetY: event.clientY - top,
        };
        setDicePosition({ left, top });
        document.body.classList.add('diceDragging');
        event.preventDefault();
    };


    if(openDice) {
        return (
            <div
                className='diceMenuContainer grow'
                ref={diceWindowRef}
                style={dicePosition ? {
                    left: `${dicePosition.left}px`,
                    top: `${dicePosition.top}px`,
                    bottom: 'auto',
                } : undefined}
            >
                <div className='diceDragHandle' onPointerDown={handlePointerDown} aria-label='Drag dice window'>
                    <span />
                    <span  style={{margin: '0px 3px'}}/>
                    <span />
                </div>
                <div className='diceMenus'>
                    <div className='diceMenu'>
                        {diceStrings.map(d => {
                            return (
                                <>
                                    {d === 'D20' && <hr className='diceLineSeperator'/>}
                                    <div className='diceControl' key={d}>
                                        <button className='dice' style={{justifyContent: selectedDice[d] ? 'space-between' : 'center', fontSize: d === 'D20' ? '1rem' : ''}} type='button' onClick={() => selectDie(d)}>
                                            {d}
                                            {selectedDice[d] && (
                                                <span className='diceCount'>{selectedDice[d]}</span>
                                            )}
                                        </button>
                                        {selectedDice[d] && (
                                            <button
                                                className='diceRemove'
                                                type='button'
                                                aria-label={`Remove one ${d}`}
                                                onClick={() => setSelectedDice(current => {
                                                    const next = { ...current };
                                                    if (next[d] === 1) {
                                                        delete next[d];
                                                    } else {
                                                        next[d] -= 1;
                                                    }
                                                    return next;
                                                })}
                                            >
                                                &minus;
                                            </button>
                                        )}
                                    </div>
                                </>
                            ) 
                        })}
                        <button className='diceRoll' type='button' onClick={() => rollDice()} disabled={isRolling || Object.keys(selectedDice).length === 0}>
                            Roll
                        </button>
                    </div>
                    <div className='diceResultsMenu'>
                        <div className='diceResultsHeader'>
                            <span className='diceResultsTitle'>Dice Rolls</span>
                            <span className='diceResultsCount' onClick={() => setOpenDice(false)}>❌</span>
                        </div>
                        <hr className='lineSeperator'/>
                        <div className='diceResults' ref={diceResultsMenuRef}>
                            {diceResults.length === 0 && (
                                <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
                                    <b className='diceEmptyState'>Get Rollin'</b>
                                    <img className='diceSpinner' src={diceSpinner} alt='Rolling dice' />
                                </div>
                            )}
                            {diceResults.map((dr, index) => {
                                let current = index === 0

                                let critCheck = (dr.groups.length === 1 && dr.diceMax === 20) && ((current && !isRolling) || (!current))
                                let critFail = critCheck && (dr.total - dr.rollMod) === 1
                                let critSucc = critCheck && (dr.total - dr.rollMod) === 20

                                let nonCritCurrentStyle = (current && !critSucc) ? {color: '#ffffff', textShadow: '1px 1px 2px #292720'} : {}

                                return (
                                    <div className={`diceResult${current ? ' current grow' : ''} `} key={`${dr.notation}-${dr.total}-${index}`}>
                                        <div className='diceResultSummary'>
                                            <span className='diceResultDie'>{dr.notation} 
                                                {dr.rollMod > 0 && <span className='diceResultMod modPos'> + {dr.rollMod}</span>}
                                                {dr.rollMod < 0 && <span className='diceResultMod modNeg'> - {dr.rollMod.toString().slice(1)}</span>}
                                            </span>
                                            {((current && !isRolling) || (!current)) &&
                                                <div className={`diceIndividualRolls ${current ? 'grow' : ''}`}>
                                                    {dr.groups.map((group, groupIndex) =>  (
                                                        <>
                                                            <span
                                                                className={`diceIndividualRoll ${critSucc ? 'crit' : ''} ${critFail ? 'critFail' : ''}`}
                                                                key={`${group.notation}-${index}`}
                                                            >
                                                                {group.rollResults}
                                                            </span>
                                                            {groupIndex !== dr.groups.length-1 && <span style={{fontSize: ".7rem"}}>+</span>}
                                                        </>
                                                    ))}
                                                </div>
                                            }
                                        </div>
                                        <span className={`diceResultContainer ${critSucc ? 'rainbowTotal' : `${critFail ? 'sadTotal' : ''}`}`}>
                                            {(current && isRolling) ? (
                                                <>
                                                    <img className='diceSpinner' src={diceSpinner} alt='Rolling dice' />
                                                </>
                                            ) : (
                                                <>
                                                    <div className='diceResultValue' style={nonCritCurrentStyle}>{dr.total}</div>
                                                </>
                                            )}
                                            <div className='diceResultType' style={nonCritCurrentStyle}>{dr.rollType && dr.rollType}</div>
                                        </span>
                                    </div>
                                )
                            })}
                        </div>
                    </div>
                </div>
            </div>
        );
    }
};

export default Dice;